import { toast } from "react-toastify";
import { storageService } from "../services/storageService";

export const checkMicPermission = async (): Promise<boolean> => {
  try {
    if (navigator.permissions) {
      const permission = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });

      if (permission.state === "granted") {
        return true;
      }

      if (permission.state === "denied") {
        toast.error(
          "Microphone access is blocked. Please enable it in browser settings.",
        );
        return false;
      }
    }

    await navigator.mediaDevices.getUserMedia({ audio: true });
    return true;
  } catch (err) {
    toast.error("Microphone permission is required to make a call.");
    return false;
  }
};

export const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
  };
  return new Date(dateString).toLocaleDateString("en-GB", options);
};

export const formatTime = (timeString: string) => {
  return new Date(timeString).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export const formatTimestamp = (dateString?: any) => {
  if (!dateString) return "Invalid Date"; // Handle null/undefined

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Invalid Date"; // Handle invalid dates

  return (
    date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }) +
    " " +
    date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  );
};

const getPhoneNumber = (chat: any) => {
  const userId = storageService.getUserId();

  // this is for when creating a new dummy chat
  if (!chat?.firstId && !chat?.secondId) {
    return chat?.firstUserPhoneNumber;
  }

  if (chat?.firstId === userId) {
    return chat?.secondUserPhoneNumber;
  } else if (chat?.secondId === userId) {
    return chat?.firstUserPhoneNumber;
  }
};

const getSavedName = (chat: any): string => {
  const userId = storageService.getUserId();
  if (!chat || !userId) return "";

  const format = (phone: string, name: string) => {
    phone = phone ?? "";
    name = name ?? "";

    // If both are same (case-insensitive, trimmed)
    if (phone.trim().toLowerCase() === name.trim().toLowerCase()) {
      return phone.trim() || name.trim();
    }

    return `${phone} ${name}`.trim();
  };

  if (chat?.firstId === userId) {
    return format(chat.secondUserPhoneNumber, chat.secondUserName);
  }

  if (chat?.secondId === userId) {
    return format(chat.firstUserPhoneNumber, chat.firstUserName);
  }

  // fallback
  return (
    chat?.firstUserPhoneNumber ||
    chat?.secondUserPhoneNumber ||
    chat?.sourceUserName ||
    ""
  );
};

export const formatChats = (response: any) => {
  if (!response) return;
  const unformattedChats = response;

  return unformattedChats?.map((chat: any) => ({
    id: chat._id,
    firstUserId: chat?.firstId,
    secondUserId: chat?.secondId,
    // this name will be used only for group chats and broadcasts
    name: chat.isGroup
      ? chat.groupName
      : chat.isBroadCast
        ? chat.groupName
        : "",
    number: chat.isGroup ? null : getPhoneNumber(chat),
    mediaType: chat?.mediaType,
    message:
      chat.mediaType === 0 && chat.lastMessage
        ? chat.lastMessage
        : chat.mediaType === 1
          ? "Sent a Photo"
          : chat.mediaType === 2
            ? "sent a Video"
            : chat.mediaType === 3
              ? "Shared a File"
              : chat.mediaType === 4
                ? "Shared an audio"
                : chat.mediaType === 6
                  ? "Voicecall"
                  : "Sent something",
    time: formatTime(chat.lastUpdate),
    date: formatDate(chat.lastUpdate),
    isMuted: chat.isMuted === "1" ? true : false,
    isPin: chat.isPin === "1" ? true : false,
    isArchived: chat.isArchived === "1" ? true : false,
    lastUpdateToSort: chat.lastUpdateToSort,
    isGroup: chat.isGroup,
    isBroadCast: chat.isBroadCast,
    isBlocked: Boolean(chat.block),
    groupMembers: chat.isGroup ? chat.groupMembers : [],
    broadCastMembers: chat.isBroadCast ? chat.broadCastMembers : [],
    sourceNumber: chat.sourceNumber,
    iseFone: chat.iseFone,
    image: chat.isGroup ? chat.groupImage : chat.userProfileImage,
    unreadCount: chat.unreadCount,
    groupSettings: chat.groupSetting,
    pastMembers: chat.pastMembers,
    isNewChat: chat?.isNewChat || null,
    savedName: chat?.savedName || getSavedName(chat),
  }));
};

export const formatSingleChat = (chat: any) => {
  const formatted: any = {
    id: chat?._id,
  };

  if (chat?.firstId !== undefined) formatted.firstUserId = chat.firstId;
  if (chat?.secondId !== undefined) formatted.secondUserId = chat.secondId;
  if (chat?.isGroup !== undefined || chat?.isBroadCast !== undefined) {
    formatted.name = chat?.isGroup
      ? chat?.groupName
      : chat?.isBroadCast
        ? chat?.groupName
        : "";
    formatted.isGroup = chat?.isGroup;
    formatted.isBroadCast = chat?.isBroadCast;
  }
  if (chat?.isGroup === false) formatted.number = getPhoneNumber(chat);
  if (chat?.mediaType !== undefined) {
    formatted.mediaType = chat.mediaType;
    formatted.message =
      chat.mediaType === 0 && chat.lastMessage
        ? chat.lastMessage
        : chat.mediaType === 1
          ? "Sent a Photo"
          : chat.mediaType === 2
            ? "sent a Video"
            : chat.mediaType === 3
              ? "Shared a File"
              : chat.mediaType === 4
                ? "Shared an audio"
                : chat.mediaType === 6
                  ? "Voicecall"
                  : "Sent something";
  }
  if (chat?.lastUpdate) {
    formatted.time = formatTime(chat.lastUpdate);
    formatted.date = formatDate(chat.lastUpdate);
  }
  if (chat?.isMuted !== undefined) formatted.isMuted = chat.isMuted === "1";
  if (chat?.isPin !== undefined) formatted.isPin = chat.isPin === "1";
  if (chat?.isArchived !== undefined)
    formatted.isArchived = chat.isArchived === "1";
  if (chat?.lastUpdateToSort)
    formatted.lastUpdateToSort = chat.lastUpdateToSort;
  if (chat?.block !== undefined) formatted.isBlocked = Boolean(chat.block);
  if (chat?.groupMembers?.length > 0)
    formatted.groupMembers = chat.groupMembers;
  if (chat?.broadCastMembers?.length > 0)
    formatted.broadCastMembers = chat.broadCastMembers;
  if (chat?.sourceNumber !== undefined)
    formatted.sourceNumber = chat.sourceNumber;
  if (chat?.iseFone !== undefined) formatted.iseFone = chat.iseFone;
  if (chat?.isGroup !== undefined)
    formatted.image = chat.isGroup ? chat.groupImage : chat.userProfileImage;
  if (chat?.unreadCount !== undefined) formatted.unreadCount = chat.unreadCount;
  if (chat?.groupSetting !== undefined)
    formatted.groupSettings = chat.groupSetting;
  if (chat?.pastMembers?.length > 0) formatted.pastMembers = chat.pastMembers;
  if (chat?.isNewChat !== undefined) formatted.isNewChat = chat.isNewChat;
  if (chat?.savedName !== undefined) formatted.savedName = chat.savedName;
  else if (chat?._id) formatted.savedName = getSavedName(chat);
  
  const senderId = chat?.ofUser || chat?.lastSender || chat?.lastMessageSenderId;
  if (senderId !== undefined) formatted.ofUser = senderId;

  return formatted;
};

export const formatMessage = (msg: any, userId?: string | null) => {
  return {
    id: msg?._id,
    senderId: msg?.ofUser,
    message: msg?.content,
    lastMessage:
      msg?.mediaType === 0 && msg?.content
        ? msg?.content
        : msg?.mediaType === 1
          ? "Sent a Photo"
          : msg?.mediaType === 2
            ? "sent a Video"
            : msg?.mediaType === 3
              ? "Shared a File"
              : msg?.mediaType === 4
                ? "Shared an audio"
                : msg?.mediaType === 6
                  ? "Voicecall"
                  : "Sent something",
    timestamp: formatTimestamp(msg?.createdAt),
    time: formatTime(msg?.createdAt),
    date: formatDate(msg?.createdAt),
    phone: "N/A",
    ...(userId && { isUser: String(msg?.ofUser) === String(userId) }),
    filePath: msg?.filePath,
    mediaType: msg?.mediaType,
    createdAt: msg?.createdAt,
    replyMessage: msg?.replyMessage || msg?.replyMsg,
    replyToId: msg?.replyToId || msg?.replyMessageId,
    number: msg?.ofUserNumber,
    replyMediaType: msg?.replyMediaType,
    replyFilePath: msg?.replyFilePath,
    scheduleTime: msg?.scheduleTime,
    isPoll: msg?.poll?.options?.length ? true : false,
    pollDetails: msg?.poll,
    isForwarded: msg?.isForwarded || false,
  };
};
export const isUserAtBottom = (ref: any) => {
  const el = ref.current;
  if (!el) return false;
  return el.scrollHeight - el.scrollTop - el.clientHeight < 50;
};

export const validateEmail = (email: string) => {
  return /\S+@\S+\.\S+/.test(email.trim());
};

export const generateHexUdid = () => {
  return crypto.randomUUID().replace(/-/g, "").slice(0, 16);
};

export const checkIfContactSaved = (number: string, contactList: any) => {
  const savedContact = contactList?.find(
    (contact: any) => contact.phoneNumber === number,
  );
  if (savedContact) {
    return savedContact.fullName;
  } else {
    return number;
  }
};

export const formatTextMessage = (msg: any, contactList?: any) => {
  if (msg.mediaType === -1) {
    const matches = msg.message.match(/\{([+\d]+)\}/g);
    const numbers = matches?.map((m: any) => m.replace(/[{}]/g, ""));
    const isAdded = msg.message.includes("added");

    if (numbers && numbers.length === 2) {
      const contact1Name = checkIfContactSaved(numbers[0], contactList);
      const contact2Name = checkIfContactSaved(numbers[1], contactList);

      if (numbers?.[0] === numbers?.[1] && msg.isUser) {
        return "You left";
      } else if (numbers?.[0] === numbers?.[1]) {
        return `${contact1Name} left`;
      } else {
        return isAdded
          ? `${contact1Name} added ${contact2Name}`
          : `${contact1Name} removed ${contact2Name}`;
      }
    }
  } else if (msg.mediaType === 6) {
  }

  return msg.message;
};

export const extractNumberFromSip = (sip: string) => {
  const match = sip.match(/sip:([^@]+)/);
  return match ? match[1] : sip;
};

export function sortChats(chats: any[]) {
  return chats?.sort((a, b) => {
    const pinDiff = Number(b.isPin ?? false) - Number(a.isPin ?? false);
    if (pinDiff !== 0) return pinDiff;

    const aTime = a.lastUpdateToSort
      ? new Date(a.lastUpdateToSort).getTime()
      : 0;
    const bTime = b.lastUpdateToSort
      ? new Date(b.lastUpdateToSort).getTime()
      : 0;
    return bTime - aTime;
  });
}
